import LatLng from "./latlng";
import { convert } from "./convert";

/**
 * Returns the centroid (geometric center) of a polygon described by a path of
 * vertices. Uses the planar area-weighted centroid formula treating lng as x
 * and lat as y. At jobsite scale (a few hundred meters) the planar
 * approximation is accurate enough and keeps the math simple and deterministic.
 *
 * Falls back to the arithmetic mean of the vertices when the polygon is
 * degenerate (zero area: a single point or collinear vertices), where the
 * area-weighted formula is undefined.
 *
 * @param {LatLngLike[]} path Polygon vertices (closing vertex optional).
 * @returns {LatLng} The polygon centroid.
 */
export default function computeCentroid(path) {
  if (!path || path.length === 0) {
    throw new TypeError("Cannot compute centroid of an empty path");
  }

  const points = path.map((v) => convert(v));

  // Drop the closing vertex when the ring is explicitly closed (first === last): the shoelace loop
  // closes the ring itself, and a duplicated vertex would skew the degenerate-polygon mean fallback.
  const first = points[0];
  const last = points[points.length - 1];

  if (points.length > 1 && first.lat() === last.lat() && first.lng() === last.lng()) {
    points.pop();
  }

  if (points.length < 3) {
    return meanCentroid(points);
  }

  // Shoelace on absolute lat/lng loses precision badly at jobsite scale: the cross products are
  // ~1e3 while their sum is ~1e-8, so the cancellation error reaches tens of meters on a small
  // polygon. Working relative to the first vertex keeps every term at the same magnitude.
  const originLng = points[0].lng();
  const originLat = points[0].lat();

  let twiceArea = 0;
  let x = 0;
  let y = 0;

  for (let i = 0; i < points.length; i++) {
    const currentLng = points[i].lng() - originLng;
    const currentLat = points[i].lat() - originLat;
    const nextLng = points[(i + 1) % points.length].lng() - originLng;
    const nextLat = points[(i + 1) % points.length].lat() - originLat;

    const cross = currentLng * nextLat - nextLng * currentLat;

    twiceArea += cross;
    x += (currentLng + nextLng) * cross;
    y += (currentLat + nextLat) * cross;
  }

  if (twiceArea === 0) {
    return meanCentroid(points);
  }

  const factor = 1 / (3 * twiceArea);

  return new LatLng(y * factor + originLat, x * factor + originLng);
}

/**
 * Arithmetic mean of the vertices. Used as the degenerate-polygon fallback.
 * @param {LatLng[]} points
 * @returns {LatLng}
 */
function meanCentroid(points) {
  const sum = points.reduce(
    (acc, point) => ({ lat: acc.lat + point.lat(), lng: acc.lng + point.lng() }),
    { lat: 0, lng: 0 }
  );

  return new LatLng(sum.lat / points.length, sum.lng / points.length);
}
